import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useIonAlert } from '@ionic/react';
import { CalendarX, Pause, Play, Wallet, XCircle } from 'lucide-react';
import { formatMoney } from '@tcrd/shared';
import { Badge, Button, Card, cn, ErrorState, Notice, RideStatusBadge, SectionTitle, Spinner } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtTime } from '../../lib/format';
import { BILLING_LABEL, SERIES_STATUS, type RecurringSeries } from './types';

export function RecurringDetailPage({ base }: { base: '/app' | '/driver' }) {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const [alert] = useIonAlert();
  const q = useQuery({ queryKey: ['recurring', id], queryFn: () => api<RecurringSeries>(`/recurring-rides/${id}`) });
  const s = q.data;

  const run = (path: string, body?: unknown, ok = 'Listo') =>
    api(`/recurring-rides/${id}/${path}`, { method: path.startsWith('occurrences/') ? 'PATCH' : 'POST', body: body ?? {} })
      .then(() => Promise.all([qc.invalidateQueries({ queryKey: ['recurring'] }), qc.invalidateQueries({ queryKey: ['rides'] })]))
      .then(() => toast(ok, 'success'))
      .catch((e) => toast(errorMessage(e), 'danger'));

  const upcoming = s?.occurrences?.filter((o) => new Date(o.scheduledAt).getTime() > Date.now() - 3600_000) ?? [];
  const byDate = new Map<string, NonNullable<RecurringSeries['occurrences']>>();
  upcoming.forEach((o) => byDate.set(o.date, [...(byDate.get(o.date) ?? []), o]));

  return (
    <Page title={s?.title ?? 'Viaje recurrente'} back={`${base}/recurring`} onRefresh={() => q.refetch()}>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {s && (
        <div className="flex flex-col gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold">{s.patternLabel}</p>
              <Badge tone={SERIES_STATUS[s.status][1]}>{SERIES_STATUS[s.status][0]}</Badge>
            </div>
            <p className="text-sm text-[var(--text-muted)]">Ida {s.outboundTime}{s.returnTime ? ` · Regreso ${s.returnTime}` : ''} · desde {s.startDate}{s.endDate ? ` hasta ${s.endDate}` : ' (continua)'}</p>
            <p className="text-sm mt-2">{s.origin.address} → {s.destination.address}</p>
            {s.pricing && <p className="mt-3 font-semibold">{BILLING_LABEL[s.pricing.billingMode]}: {formatMoney(s.pricing.amount)}{s.pricing.billingMode === 'PER_RIDE' ? ' por trayecto' : ''}</p>}
            {s.status === 'PENDING_QUOTE' && (
              <Notice>Esperando la cotización del conductor. {base === '/app' ? 'Te avisaremos cuando llegue.' : ''} <Link className="underline font-semibold" to={`${base === '/app' ? '/app/requests' : '/driver/requests'}/${s.requestId}`}>Ver solicitud</Link></Notice>
            )}
          </Card>

          {s.packages?.map((p) => (
            <Card key={p.id} className="p-5">
              <SectionTitle title={`Paquete ${BILLING_LABEL[p.billingMode]?.toLowerCase()}`} subtitle={`${p.periodStart} a ${p.periodEnd}`} />
              <dl className="grid grid-cols-2 gap-y-1 text-sm">
                <dt className="text-[var(--text-muted)]">Viajes incluidos</dt><dd className="text-right">{p.ridesIncluded ?? 'Sin límite'}</dd>
                <dt className="text-[var(--text-muted)]">Programados</dt><dd className="text-right">{p.ridesScheduled}</dd>
                <dt className="text-[var(--text-muted)]">Realizados</dt><dd className="text-right">{p.ridesCompleted}</dd>
                <dt className="text-[var(--text-muted)]">Precio</dt><dd className="text-right">{formatMoney(p.amount)}</dd>
                {p.payment && (<><dt className="text-[var(--text-muted)]">Pagado</dt><dd className="text-right">{formatMoney(p.payment.farePaid)}</dd><dt className="font-semibold">Saldo</dt><dd className="text-right font-semibold">{formatMoney(p.payment.balance)}</dd></>)}
              </dl>
              {base === '/driver' && p.payment && Number(p.payment.balance) > 0 && (
                <Link to={`/driver/payments/${p.payment.id}`}><Button className="mt-3" block variant="accent" icon={<Wallet className="size-5" />}>Registrar pago del paquete</Button></Link>
              )}
            </Card>
          ))}
          {s.perRideTotals && (
            <Card className="p-4 text-sm flex justify-between"><span>Viajes realizados · pagado {formatMoney(s.perRideTotals.farePaid)}</span><span className="font-semibold">Saldo {formatMoney(s.perRideTotals.balance)}</span></Card>
          )}

          {s.canManage && ['ACTIVE', 'PAUSED'].includes(s.status) && (
            <div className="grid grid-cols-2 gap-2">
              {s.status === 'ACTIVE' ? (
                <Button variant="secondary" icon={<Pause className="size-5" />} onClick={() => alert({ header: '¿Pausar la serie?', message: 'Se cancelarán los viajes programados hasta reactivarla.', buttons: [{ text: 'No', role: 'cancel' }, { text: 'Pausar', handler: () => void run('pause', undefined, 'Serie pausada') }] })}>Pausar</Button>
              ) : (
                <Button variant="secondary" icon={<Play className="size-5" />} onClick={() => run('resume', undefined, 'Serie reactivada')}>Reactivar</Button>
              )}
              <Button variant="danger" icon={<XCircle className="size-5" />} onClick={() => alert({ header: '¿Cancelar la serie completa?', buttons: [{ text: 'No', role: 'cancel' }, { text: 'Cancelar serie', role: 'destructive', handler: () => void run('cancel', undefined, 'Serie cancelada') }] })}>Cancelar serie</Button>
            </div>
          )}

          {byDate.size > 0 && (
            <section>
              <SectionTitle title="Próximos viajes" />
              <div className="flex flex-col gap-2">
                {[...byDate.entries()].map(([date, occs]) => (
                  <Card key={date} className="p-3">
                    <div className="flex items-center justify-between">
                      <p className="font-medium capitalize">{new Date(`${date}T12:00:00-04:00`).toLocaleDateString('es-DO', { weekday: 'long', day: 'numeric', month: 'short' })}</p>
                      {s.canManage && s.status === 'ACTIVE' && occs.some((o) => o.status === 'RIDE_CREATED') && (
                        <button className="tap text-xs text-[var(--text-muted)] flex items-center gap-1" onClick={() => alert({ header: `No viajar el ${date}`, inputs: [{ name: 'note', type: 'text', placeholder: 'Motivo (opcional)' }], buttons: [{ text: 'Volver', role: 'cancel' }, { text: 'Excluir fecha', handler: (v: { note: string }) => void run('exceptions', { date, note: v.note || undefined }, 'Fecha excluida') }] })}>
                          <CalendarX className="size-4" aria-hidden /> Excluir
                        </button>
                      )}
                    </div>
                    {occs.map((o) => (
                      <div key={o.id} className={cn('mt-2 flex items-center justify-between gap-2 text-sm', o.status !== 'RIDE_CREATED' && 'opacity-60')}>
                        <span>{o.leg === 'RETURN' ? 'Regreso' : 'Ida'} · {fmtTime(o.scheduledAt)}</span>
                        {o.status === 'SKIPPED' && <Badge tone="warning">Conflicto de agenda</Badge>}
                        {o.status === 'CANCELLED' && <Badge>Cancelado</Badge>}
                        {o.ride && o.status === 'RIDE_CREATED' && (
                          <span className="flex items-center gap-2">
                            {s.canManage && o.ride.status === 'CONFIRMED' && (
                              <button
                                className="tap text-xs underline"
                                onClick={() => alert({ header: 'Cambiar hora de este viaje', inputs: [{ name: 'time', type: 'time', value: fmtTime(o.scheduledAt).length <= 5 ? fmtTime(o.scheduledAt) : undefined }], buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Guardar', handler: (v: { time: string }) => void run(`occurrences/${o.id}`, { time: v.time }, 'Hora actualizada') }] })}
                              >
                                Cambiar hora
                              </button>
                            )}
                            <Link to={`${base}/rides/${o.ride.id}`}><RideStatusBadge status={o.ride.status as never} /></Link>
                          </span>
                        )}
                      </div>
                    ))}
                  </Card>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </Page>
  );
}
